import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { invoiceAPI } from 'redux/invoiceAPI';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import {
  FetchingStateWithDoNotRepeat,
  InvoiceNumberObject,
  InvoiceNumberAndDeliveryTypeObject,
} from '@payer-admin-ui/components-v2/src/types/state';
import { fetchInvoice } from '../../views/Pages/invoices/invoicesGlobalState';
import { MerchantAdminDispatch } from '../../redux/store';

export const sendCopy = createAsyncThunk<void, InvoiceNumberAndDeliveryTypeObject>(
  'invoices/sendCopy',
  async ({ invoiceNumber, deliveryType }) => {
    await invoiceAPI.actions.sendCopy({ invoiceNumber, deliveryType });
  },
);

export const sendReminder = createAsyncThunk<
  void,
  InvoiceNumberAndDeliveryTypeObject,
  { dispatch: MerchantAdminDispatch }
>('invoices/sendReminder', async ({ invoiceNumber, deliveryType }, { dispatch }) => {
  await invoiceAPI.actions.sendReminder({ invoiceNumber, deliveryType });
  dispatch(fetchInvoice({ invoiceNumber }));
});

export const sendReminderByInvoiceNumber = createAsyncThunk<
  void,
  InvoiceNumberObject,
  { dispatch: MerchantAdminDispatch }
>('invoices/sendReminderByInvoiceNumber', async ({ invoiceNumber }, { dispatch }) => {
  const invoice = await invoiceAPI.fetchByInvoiceNumber({ invoiceNumber });
  await invoiceAPI.actions.sendReminder({ invoiceNumber: invoice.invoiceNumber, deliveryType: invoice.deliveryType });
  dispatch(fetchInvoice({ invoiceNumber }));
});

export const cancelReminders = createAsyncThunk<void, InvoiceNumberObject, { dispatch: MerchantAdminDispatch }>(
  'invoices/cancelReminders',
  async ({ invoiceNumber }, { dispatch }) => {
    await invoiceAPI.actions.cancelReminders({ invoiceNumber });
    dispatch(fetchInvoice({ invoiceNumber }));
  },
);

export const stopDebtCollection = createAsyncThunk<void, InvoiceNumberObject, { dispatch: MerchantAdminDispatch }>(
  'invoices/stopDebtCollection',
  async ({ invoiceNumber }, { dispatch }) => {
    await invoiceAPI.actions.stopDebtCollection({ invoiceNumber });
    dispatch(fetchInvoice({ invoiceNumber }));
  },
);

export const creditFeeBalance = createAsyncThunk<void, InvoiceNumberObject, { dispatch: MerchantAdminDispatch }>(
  'invoices/creditFeeBalance',
  async ({ invoiceNumber }, { dispatch }) => {
    await invoiceAPI.actions.creditFeeBalance({ invoiceNumber });
    dispatch(fetchInvoice({ invoiceNumber }));
  },
);

export const approveOrder = createAsyncThunk<void, InvoiceNumberObject, { dispatch: MerchantAdminDispatch }>(
  'invoices/approveOrder',
  async ({ invoiceNumber }, { dispatch }) => {
    await invoiceAPI.actions.approveOrder({ invoiceNumber });
    dispatch(fetchInvoice({ invoiceNumber }));
  },
);

export const sendForDebtCollection = createAsyncThunk<void, InvoiceNumberObject, { dispatch: MerchantAdminDispatch }>(
  'invoices/sendForDebtCollection',
  async ({ invoiceNumber }, { dispatch }) => {
    await invoiceAPI.actions.sendForDebtCollection({ invoiceNumber });
    dispatch(fetchInvoice({ invoiceNumber }));
  },
);

export type InvoiceActionsState = {
  sendCopyState: { [invoiceNumber in string]?: { [deliveryType in DeliveryType]?: FetchingStateWithDoNotRepeat } };
  sendReminderState: { [invoiceNumber in string]?: FetchingStateWithDoNotRepeat };
  cancelRemindersState: { [invoiceNumber in string]?: FetchingStateWithDoNotRepeat };
  stopDebtCollectionState: { [invoiceNumber in string]?: FetchingStateWithDoNotRepeat };
  approveOrderState: { [invoiceNumber in string]?: FetchingStateWithDoNotRepeat };
  sendForDebtCollectionState: { [invoiceNumber in string]?: FetchingStateWithDoNotRepeat };
  creditFeeBalanceState: { [invoiceNumber in string]?: FetchingStateWithDoNotRepeat };
  actionError: any;
};

const invoiceActionsSlice = createSlice({
  name: 'invoiceActions',
  initialState: {
    sendCopyState: {},
    sendReminderState: {},
    cancelRemindersState: {},
    stopDebtCollectionState: {},
    approveOrderState: {},
    sendForDebtCollectionState: {},
    creditFeeBalanceState: {},
    actionError: null,
  } as InvoiceActionsState,
  reducers: {
    resetError: state => {
      state.actionError = null;
    },
    resetSendCopySuccessState: (
      state,
      { payload: { invoiceNumber, deliveryType } }: PayloadAction<InvoiceNumberAndDeliveryTypeObject>,
    ) => {
      delete state.sendCopyState[invoiceNumber]?.[deliveryType]?.success;
    },
    resetSendReminderSuccessState: (state, { payload: { invoiceNumber } }: PayloadAction<InvoiceNumberObject>) => {
      delete state.sendReminderState[invoiceNumber]?.success;
    },
    resetCancelRemindersSuccessState: (state, { payload: { invoiceNumber } }: PayloadAction<InvoiceNumberObject>) => {
      delete state.cancelRemindersState[invoiceNumber]?.success;
      delete state.cancelRemindersState[invoiceNumber]?.doNotRepeat;
    },
    resetStopDebtCollectionSuccessState: (
      state,
      { payload: { invoiceNumber } }: PayloadAction<InvoiceNumberObject>,
    ) => {
      delete state.stopDebtCollectionState[invoiceNumber]?.success;
      state.stopDebtCollectionState[invoiceNumber] = {
        isFetching: false,
        ...state.stopDebtCollectionState[invoiceNumber],
        doNotRepeat: true,
      };
    },
    resetApproveOrderSuccessState: (state, { payload: { invoiceNumber } }: PayloadAction<InvoiceNumberObject>) => {
      delete state.approveOrderState[invoiceNumber]?.success;
      state.approveOrderState[invoiceNumber] = {
        isFetching: false,
        ...state.approveOrderState[invoiceNumber],
        doNotRepeat: true,
      };
    },
    resetSendForDebtCollectionSuccessState: (
      state,
      { payload: { invoiceNumber } }: PayloadAction<InvoiceNumberObject>,
    ) => {
      delete state.sendForDebtCollectionState[invoiceNumber]?.success;
      state.sendForDebtCollectionState[invoiceNumber] = {
        isFetching: false,
        ...state.sendForDebtCollectionState[invoiceNumber],
        doNotRepeat: true,
      };
    },
    resetCreditFeeBalanceSuccessState: (state, { payload: { invoiceNumber } }: PayloadAction<InvoiceNumberObject>) => {
      delete state.creditFeeBalanceState[invoiceNumber]?.success;
      state.creditFeeBalanceState[invoiceNumber] = {
        isFetching: false,
        ...state.creditFeeBalanceState[invoiceNumber],
        doNotRepeat: true,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(sendCopy.pending, (state, { meta: { arg } }) => {
      state.sendCopyState[arg.invoiceNumber] = {
        ...state.sendCopyState[arg.invoiceNumber],
        [arg.deliveryType]: { isFetching: true, success: undefined },
      };
    });
    builder.addCase(sendCopy.fulfilled, (state, { meta: { arg } }) => {
      state.sendCopyState[arg.invoiceNumber] = {
        ...state.sendCopyState[arg.invoiceNumber],
        [arg.deliveryType]: { isFetching: false, success: true },
      };
    });
    builder.addCase(sendCopy.rejected, (state, { meta: { arg }, error }) => {
      state.sendCopyState[arg.invoiceNumber] = {
        ...state.sendCopyState[arg.invoiceNumber],
        [arg.deliveryType]: {
          ...state.sendCopyState[arg.invoiceNumber]?.[arg.deliveryType],
          isFetching: false,
        },
      };
      state.actionError = error;
    });

    builder.addCase(sendReminder.pending, (state, { meta: { arg } }) => {
      state.sendReminderState[arg.invoiceNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(sendReminder.fulfilled, (state, { meta: { arg } }) => {
      state.sendReminderState[arg.invoiceNumber] = { isFetching: false, success: true };
    });
    builder.addCase(sendReminder.rejected, (state, { meta: { arg }, error }) => {
      state.sendReminderState[arg.invoiceNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });

    builder.addCase(sendReminderByInvoiceNumber.pending, (state, { meta: { arg } }) => {
      state.sendReminderState[arg.invoiceNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(sendReminderByInvoiceNumber.fulfilled, (state, { meta: { arg } }) => {
      state.sendReminderState[arg.invoiceNumber] = { isFetching: false, success: true };
    });
    builder.addCase(sendReminderByInvoiceNumber.rejected, (state, { meta: { arg }, error }) => {
      state.sendReminderState[arg.invoiceNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });

    builder.addCase(cancelReminders.pending, (state, { meta: { arg } }) => {
      state.cancelRemindersState[arg.invoiceNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(cancelReminders.fulfilled, (state, { meta: { arg } }) => {
      state.cancelRemindersState[arg.invoiceNumber] = { isFetching: false, success: true };
    });
    builder.addCase(cancelReminders.rejected, (state, { meta: { arg }, error }) => {
      state.cancelRemindersState[arg.invoiceNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });

    builder.addCase(stopDebtCollection.pending, (state, { meta: { arg } }) => {
      state.stopDebtCollectionState[arg.invoiceNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(stopDebtCollection.fulfilled, (state, { meta: { arg } }) => {
      state.stopDebtCollectionState[arg.invoiceNumber] = { isFetching: false, success: true };
    });
    builder.addCase(stopDebtCollection.rejected, (state, { meta: { arg }, error }) => {
      state.stopDebtCollectionState[arg.invoiceNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });

    builder.addCase(creditFeeBalance.pending, (state, { meta: { arg } }) => {
      state.stopDebtCollectionState[arg.invoiceNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(creditFeeBalance.fulfilled, (state, { meta: { arg } }) => {
      state.stopDebtCollectionState[arg.invoiceNumber] = { isFetching: false, success: true };
    });
    builder.addCase(creditFeeBalance.rejected, (state, { meta: { arg }, error }) => {
      state.stopDebtCollectionState[arg.invoiceNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });

    builder.addCase(approveOrder.pending, (state, { meta: { arg } }) => {
      state.approveOrderState[arg.invoiceNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(approveOrder.fulfilled, (state, { meta: { arg } }) => {
      state.approveOrderState[arg.invoiceNumber] = { isFetching: false, success: true };
    });
    builder.addCase(approveOrder.rejected, (state, { meta: { arg }, error }) => {
      state.approveOrderState[arg.invoiceNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });

    builder.addCase(sendForDebtCollection.pending, (state, { meta: { arg } }) => {
      state.sendForDebtCollectionState[arg.invoiceNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(sendForDebtCollection.fulfilled, (state, { meta: { arg } }) => {
      state.sendForDebtCollectionState[arg.invoiceNumber] = { isFetching: false, success: true };
    });
    builder.addCase(sendForDebtCollection.rejected, (state, { meta: { arg }, error }) => {
      state.sendForDebtCollectionState[arg.invoiceNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });
  },
});

export const {
  actions: {
    resetError,
    resetSendCopySuccessState,
    resetSendReminderSuccessState,
    resetCancelRemindersSuccessState,
    resetStopDebtCollectionSuccessState,
    resetApproveOrderSuccessState,
    resetSendForDebtCollectionSuccessState,
    resetCreditFeeBalanceSuccessState,
  },
  reducer: invoiceActionsReducer,
} = invoiceActionsSlice;
